import { observer } from 'mobx-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useSuccessReaction } from 'src/js/hook/use-reaction';
import { useTask } from 'src/js/hook/use-task';
import { RebootResponse } from 'src/js/model/api/response/reboot-response';
import { SettingsTabContent } from 'src/js/pages/settings/settings-tab-content';
import { IsSavedRef, RebootIsSaved } from 'src/js/reboot/reboot-is-saved';
import { RebootNeeded, RebootNeededRef } from 'src/js/reboot/reboot-needed';
import { MakitoPaths } from 'src/js/route';
import { createValidateNtpServerTask } from 'src/js/task/settings-date-time-tasks';
import { createRebootTask } from 'src/js/task/settings-tasks';

import { DateAndTimeForm, DateAndTimeFormValues } from './date-and-time-form';
import { DateTime } from './date-and-time-view-model';
import { fetchDateTime, updateDateTime } from './datetime-store';
import { NtpStatisticsModal } from './ntp-statistics-modal';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { constant } from 'src/js/constant';
import { useMutable } from 'src/js/hook/use-mutable';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';

export const DateAndTime: React.FunctionComponent = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dateTime, setDateTime] = useState<DateTime>();
  const [fastRefresh, setFastRefresh] = useMutable(false);
  const [fastRefreshCount, setFastRefreshCount] = useMutable(0);
  const [manualTimeWasChanged, setManualTimeWasChanged] = useState(false);

  useAdaptiveFetch(
    () =>
      fetchDateTime(setDateTime).then((res) => {
        if (res.ok) {
          setLoading(false);
          if (fastRefreshCount() > 0) {
            if (fastRefreshCount() === 1) {
              setFastRefresh(false); // off
            }
            setFastRefreshCount(fastRefreshCount() - 1);
          }
        }
      }),
    fastRefreshCount() > 0 ? 1000 : constant.defaultRefreshInterval,
    [fastRefresh()],
  );

  const rebootTask = useTask(createRebootTask());
  const validateNtpServerTask = useTask(createValidateNtpServerTask(t));
  const [statisticsModalShown, showStatisticsModal, hideStatisticsModal] = useModalControls();

  const handleSubmit = (data: DateAndTimeFormValues) => {
    const initials = dateTime;
    let ptpOnly = !!systemInfoCapabilities.is2110();
    //console.log('time', initials, data);
    //const initial_time_txt = initials.time?.format('M D YYYY HH:mm:ss');
    //const update_time_txt = data.time?.format('M D YYYY HH:mm:ss');
    if (
      initials.ntp !== data.ntp ||
      (data.ntp === false && manualTimeWasChanged) ||
      initials.timezone !== data.timezone ||
      initials.server !== data.server
    ) {
      ptpOnly = false;
    }
    if (manualTimeWasChanged === false && data.ntp === false) {
      // user did not change manual time, so dont send it
      delete data.time;
    }
    return updateDateTime(data, setDateTime, ptpOnly, t).then((res) => {
      if (res.ok) {
        // reboot needed ?
        if (data.ntp !== initials.ntp || data.timezone !== initials.timezone) {
          rebootNeededRef.current.show();
          // form re-init is in onLocalSubmit() in other file
        }
        setFastRefreshCount(5);
        setFastRefresh(true);
      }
      return res;
    });
  };

  useSuccessReaction(rebootTask, (result: RebootResponse) => {
    navigate(result.upgrade ? MakitoPaths.rebootingUpgrade : MakitoPaths.rebooting);
  });

  const rebootNeededRef = useRef<RebootNeededRef>(null);

  // the reboot handling for the reboot button
  const childRef = useRef<IsSavedRef>(null);
  const onRebootClicked = (): void => {
    childRef.current.show();
  };

  return (
    <SettingsTabContent name={t('settings.dateTime.title')} id="datetime">
      {dateTime && (
        <DateAndTimeForm
          loading={loading}
          //  data={isNil(dateTime) ? createDefaultDateTime() : dateTime}
          data={dateTime}
          translator={t}
          onReboot={onRebootClicked}
          onStatistics={showStatisticsModal}
          statisticsDisabled={!dateTime?.chronydRunning ?? true}
          onSubmit={handleSubmit}
          validateNtpServerTask={validateNtpServerTask}
          setManualTimeWasChanged={setManualTimeWasChanged}
        />
      )}
      {statisticsModalShown && <NtpStatisticsModal onClose={hideStatisticsModal} />}
      <RebootIsSaved ref={childRef} />
      <RebootNeeded ref={rebootNeededRef} />
    </SettingsTabContent>
  );
});
